import React, { useState, useEffect } from "react";
import GalleryStyle from "../../../assets/stylesheets/farm-detail/sections/Gallery.module.css";
import FarmDetailStyle from "../../../assets/stylesheets/farm-detail/FarmDetailPage.module.css";
import { getGalleryImages, getCoverImage } from '../../../api/farm-service/imageService';
import { GallerySkeleton } from '../../../components/common/FarmDetailSkeleton';
import { BookmarkButton, FavoriteButton } from '../../../components/common/BookmarkFavoriteButtons';
import Logger from "../../../utils/logger";

const log = Logger.create('Gallery');

/**
 * GallerySection component displays farm cover and gallery images.
 * Optimized to use farm object data when available to avoid unnecessary API calls.
 * Falls back to individual API calls only when image data is not present in farm object.
 */
export default function GallerySection({ title, farm, farmId, subtitle }) {
  const [showList, setShowList] = useState(false);
  const [images, setImages] = useState([]);
  const [coverImage, setCoverImage] = useState({ src: "", alt: "" });
  const handleOpenList = () => setShowList(true);
  const [allLoaded, setAllLoaded] = useState(false);
  const [visibleCount, setVisibleCount] = useState(0);

  const IMAGE_RENDER_DELAY_MS = 25; // Time between each image reveal
  const IMAGE_LOAD_TIMEOUT_MS = 2000; // Timeout for each image load
  
  const effectiveFarmId = farm?.id || farmId;

  const hasData = Boolean(coverImage.src && images.length > 0);
  const itemsToRender = hasData
    ? [
        {
          src: coverImage.src,
          alt: coverImage.alt,
          className: GalleryStyle["gallery-item--large"]
        },
        ...images.slice(0, 4).map((img, i) => ({
          src: img.src,
          alt: img.alt,
          className: `${GalleryStyle["gallery-thumb"]} ${GalleryStyle[`thumb${i + 1}`]}`,
        }))
      ]
    : [
        {
          className: GalleryStyle["gallery-item--large"]
        },
        ...[1, 2, 3, 4].map((_, i) => ({
          className: `${GalleryStyle["gallery-thumb"]} ${GalleryStyle[`thumb${i + 1}`]}`
        }))
      ];

  useEffect(() => {
    if (!effectiveFarmId) return;
    log.log("Farm ID here: ", effectiveFarmId);
    
    // Try to use farm object data first if available
    if (farm?.coverImage?.viewUrl) {
      log.log("Using farm object cover image data");
      setCoverImage({
        src: farm.coverImage.viewUrl,
        alt: `${title} cover image`
      });
    } else {
      // Fallback to API call for cover image
      log.log("Fetching cover image via API call");
      getCoverImage(effectiveFarmId)
        .then(res => {
          const cv = res.viewUrl ?? res.url;
          setCoverImage({
            src: cv,
            alt: `${title} cover image`
          });
        })
        .catch(err => log.error("Failed to load cover image:", err));
    }
    
    // Try to use farm object gallery data if available
    if (farm?.galleryImages && farm.galleryImages.length > 0) {
      log.log("Using farm object gallery images data (galleryImages property)");
      setImages(farm.galleryImages.map(img => ({
        src: img.viewUrl ?? img.url,
        alt: `${title} image ${img.id}`
      })));
    } else if (farm?.images && farm.images.length > 0) {
      // Alternative property name for gallery images
      log.log("Using farm object gallery images data (images property)");
      setImages(farm.images.map(img => ({
        src: img.viewUrl ?? img.url,
        alt: `${title} image ${img.id}`
      })));
    } else {
      // Fallback to API call for gallery images
      log.log("Fetching gallery images via API call");
      getGalleryImages(effectiveFarmId)
        .then(res => {
          const imgs = res.data;
          log.log("Bulk Images return: ", imgs);
          setImages(imgs.map(img => ({
            src: img.viewUrl ?? img.url,
            alt: `${title} image ${img.id}`
          })));
        })
        .catch(err => {
          log.error("Failed to load gallery images:", err);
        });
    }
  }, [effectiveFarmId, title, farm?.coverImage, farm?.galleryImages, farm?.images]);

  const handleShare = () => {
    if (navigator.share) {
      navigator
        .share({
          title: title,
          text: subtitle,
          url: window.location.href
        })
        .then(() => log.log("Shared successfully"))
        .catch((error) => log.log("Error sharing", error));
    } else {
      alert("Sharing is not supported in this browser.");
    }
  };

  // 2) Preload both cover + thumbnail in background
  useEffect(() => {
    if (!coverImage.src || images.length === 0) return;
    const srcs = [coverImage.src, ...images.slice(0, 4).map(i => i.src)];
    let loaded = 0;
    const totalImages = srcs.length;
    
    const handleImageComplete = () => {
      loaded++;
      if (loaded === totalImages) {
        setAllLoaded(true);
      }
    };
    
    srcs.forEach(src => {
      const img = new Image();
      img.src = src;
      
      // Set timeout to prevent hanging on slow images
      const timeout = setTimeout(() => {
        handleImageComplete();
      }, IMAGE_LOAD_TIMEOUT_MS); // Timeout per image
      
      img.onload = () => {
        clearTimeout(timeout);
        handleImageComplete();
      };
      
      img.onerror = () => {
        clearTimeout(timeout);
        handleImageComplete(); // Continue even if image fails
      };
    });
  }, [coverImage, images]);

  useEffect(() => {
    if (!allLoaded) return;
    const total = 1 + Math.min(images.length, 4); // cover + images 4
    let idx = 0;
    const timer = setInterval(() => {
      setVisibleCount(++idx);
      if (idx >= total) clearInterval(timer);
    }, IMAGE_RENDER_DELAY_MS);
    return () => clearInterval(timer);
  }, [allLoaded, images]);


 return (
   // Show our comprehensive GallerySkeleton if data not loaded yet
   (!hasData || !allLoaded) ? (
     <GallerySkeleton title={title} />
   ) : (
     <div className={GalleryStyle["gallery-container"]}>
        <div className={FarmDetailStyle["farmDetail-header"]}>
          <h1 className={GalleryStyle["gallery-title"]}>{title}</h1>
          <div className={FarmDetailStyle["gallery-actions"]}>
            <button className={FarmDetailStyle["Gallery-save"]} onClick={handleShare}>
              <svg
                width="15"
                height="18"
                viewBox="0 0 19 21"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  width="16.3334"
                  height="17.5001"
                  transform="translate(1.41211 2)"
                  fill="none"
                />
                <path
                  d="M9.57881 3.16667V13.0834M13.0788 5.50001L9.57881 2L6.07879 5.50001M1.41211 11.3334V17.1667C1.41211 17.7856 1.65794 18.3791 2.09553 18.8166C2.53312 19.2542 3.12661 19.5001 3.74545 19.5001H15.4122C16.031 19.5001 16.6245 19.2542 17.0621 18.8166C17.4997 18.3791 17.7455 17.7856 17.7455 17.1667V11.3334"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Share
            </button>
            {/* <BookmarkButton
              targetType="FARM"
              targetId={effectiveFarmId}
              className={FarmDetailStyle["Gallery-save"]}
            /> */}
            <FavoriteButton
              targetType="FARM"
              targetId={effectiveFarmId}
              className={FarmDetailStyle["Gallery-save"]}
            />
          </div>


        </div>

        <div className={GalleryStyle["gallery-grid"]}>
          {itemsToRender.map((item, idx) => (
            // Apply the specific layout class to the wrapper div
            <div key={idx} className={item.className}>
              {(visibleCount <= idx) ? (
                <div className={`${GalleryStyle["photo-card"]} ${GalleryStyle.skeleton}`} />
              ) : (
                <img
                  src={item.src}
                  alt={item.alt}
                  // Apply both the specific layout class and the general photo-card styling
                  className={`${item.className} ${GalleryStyle["photo-card"]}`}
                  onClick={handleOpenList}
                />
              )}
            </div>
          ))}
        </div>
      </div>
   )
 );
}
